import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import { Pencil, Trash2 } from 'lucide-react'

import confirm from 'components/dialogConfirm'
import {
  type SenderEmail,
  useDeleteSenderEmail,
  useGetWorkspaceSenderEmails,
} from 'features/campaigns/sender_emails_api'
import { SenderEmailsTable } from 'features/campaigns/SequenceTab/SenderEmailsTable'
import { EditSenderEmailModal } from './EditSenderEmailModal'
import * as SettingsS from './styles'

const DESCRIPTION_TEXT =
  'Manage your sender email accounts. Contact support to add new sender domains.'

const SUPPORT_EMAIL = 'mailto:support@firstbite.io'

export function SenderEmailSettings() {
  const [editingSenderEmail, setEditingSenderEmail] =
    useState<SenderEmail | null>(null)

  const {
    data: senderEmails,
    isLoading,
    isFetching,
  } = useGetWorkspaceSenderEmails()

  const deleteMutation = useDeleteSenderEmail()

  const handleDelete = useCallback(
    async (senderEmail: SenderEmail) => {
      const confirmed = await confirm(
        `Are you sure you want to delete "${senderEmail.name || senderEmail.email}"?`,
        'Delete Sender Email',
        'This will permanently remove the sender email from EmailBison. This action cannot be undone.',
        { confirmLabel: 'Delete', buttonVariant: 'danger' }
      )

      if (!confirmed) return

      try {
        await deleteMutation.mutateAsync(senderEmail.id)
        toast.success('Sender email deleted successfully')
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.detail || 'Failed to delete sender email'
        toast.error(errorMessage)
      }
    },
    [deleteMutation]
  )

  const getRowActions = useCallback(
    (senderEmail: SenderEmail) => [
      {
        label: 'Edit',
        icon: Pencil,
        onClick: () => setEditingSenderEmail(senderEmail),
      },
      {
        label: 'Delete',
        icon: Trash2,
        onClick: () => handleDelete(senderEmail),
        variant: 'destructive' as const,
        disabled: deleteMutation.isPending,
      },
    ],
    [handleDelete, deleteMutation.isPending]
  )

  const tableHeader = (
    <SettingsS.SettingsTableHeader>
      <SettingsS.TableDescriptionText>
        {DESCRIPTION_TEXT}{' '}
        <a href={SUPPORT_EMAIL} className="text-brand hover:underline">
          Contact support
        </a>
      </SettingsS.TableDescriptionText>
    </SettingsS.SettingsTableHeader>
  )

  return (
    <>
      <SenderEmailsTable
        data={senderEmails?.results ?? []}
        isLoading={isLoading || isFetching}
        tableKey="settings-sender-emails-table"
        tableHeader={tableHeader}
        getRowActions={getRowActions}
      />
      {editingSenderEmail && (
        <EditSenderEmailModal
          isOpen
          onClose={() => setEditingSenderEmail(null)}
          senderEmail={editingSenderEmail}
        />
      )}
    </>
  )
}
